import React, { useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import {
  applyBulkLocationEdit,
  bulkLocationEditExportUrl,
  validateBulkLocationEdit,
} from "../requests/location-requests";
import { fetchDownloadFile } from "../../../util/FileUtils";
import type {
  BulkLocationEditApplyResult,
  BulkLocationEditPreview,
} from "../../../types/BulkLocationEdit";
import { rowHasChanges } from "../../../types/BulkLocationEdit";
import { getErrorMessage } from "../../../util/NetworkErrorUtil";

interface BulkLocationEditDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful apply so the parent can refresh the location list. */
  onApplied: (result: BulkLocationEditApplyResult) => void;
  onError: (message: string) => void;
}

/** Empty cells are easy to miss in a diff, so render them as an explicit marker. */
const renderValue = (value: string | null) => {
  if (value === null || value === "") {
    return (
      <Typography component="span" variant="caption" sx={{ color: "#8299A5", fontStyle: "italic" }}>
        empty
      </Typography>
    );
  }
  return <Typography component="span" variant="caption">{value}</Typography>;
};

/**
 * Bulk edit locations from a spreadsheet. The supplier downloads a CSV
 * pre-filled with all of their locations, edits it offline, then uploads it.
 *
 * <p>The upload is deliberately two steps: it is first validated as a dry run
 * that saves nothing, and the resulting diff is shown for confirmation before
 * anything is applied. Bulk edits touch live, bookable listings and pricing, so
 * the supplier sees exactly what will change first.
 */
const BulkLocationEditDialog = ({
  open,
  onClose,
  onApplied,
  onError,
}: BulkLocationEditDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [validating, setValidating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<BulkLocationEditPreview | null>(null);
  const [applied, setApplied] = useState<BulkLocationEditApplyResult | null>(null);

  const resetResults = () => {
    setPreview(null);
    setApplied(null);
  };

  const handleFile = async (chosen: File) => {
    setFile(chosen);
    setValidating(true);
    resetResults();
    try {
      const response = await validateBulkLocationEdit(chosen);
      setPreview(response.body as BulkLocationEditPreview);
    } catch (err) {
      onError(getErrorMessage(err) || "Could not read that file");
      setFile(null);
    } finally {
      setValidating(false);
      // Clear the input so re-choosing the same file still fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleApply = async () => {
    if (!file) return;
    setApplying(true);
    try {
      // The same file is re-sent rather than a client-side diff: the server
      // re-validates so a location changed since the preview is still caught.
      const response = await applyBulkLocationEdit(file);
      const result = response.body as BulkLocationEditApplyResult;
      setApplied(result);
      setPreview(result.preview);
      if (result.applied) {
        onApplied(result);
      } else {
        onError("Those changes could not be applied. Review the errors below.");
      }
    } catch (err) {
      onError(getErrorMessage(err) || "Could not apply those changes");
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    resetResults();
    onClose();
  };

  const handleDownloadExport = () => {
    fetchDownloadFile(
      bulkLocationEditExportUrl(),
      undefined,
      (err) => onError(getErrorMessage(err) || "Could not download your locations"),
    );
  };

  const rowsWithChanges = preview?.rows.filter(rowHasChanges) ?? [];
  const rowsWithErrors = preview?.rows.filter((row) => row.errors.length > 0) ?? [];
  const busy = validating || applying;
  const canApply = !!preview && preview.applyable && preview.fieldsChanged > 0 && !applied?.applied;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Edit Locations</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: "#5E7782", mb: 2 }}>
          Edit many locations at once in a spreadsheet. Download your locations
          below — the file comes pre-filled with each location's current details.
          Change the values you want, save as CSV, then upload it here. You will
          see exactly what will change before anything is saved.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontSize: "0.85rem" }}>How the file works</AlertTitle>
          <Typography variant="caption" component="div">
            Leave a cell <strong>blank to keep its current value</strong>. To empty
            a field out, type <strong>[CLEAR]</strong>. Do not edit the{" "}
            <strong>location_id</strong> or <strong>last_modified_date</strong>{" "}
            columns — they are how we match your rows and detect edits made from
            an out-of-date file.
          </Typography>
        </Alert>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => {
              const chosen = e.target.files?.[0];
              if (chosen) handleFile(chosen);
            }}
          />
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
          >
            {validating ? "Checking…" : "Choose CSV file"}
          </Button>
          <Link
            component="button"
            type="button"
            onClick={handleDownloadExport}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.85rem" }}
          >
            <DownloadIcon fontSize="small" /> Download my locations
          </Link>
          {file && (
            <Typography variant="caption" sx={{ color: "#8299A5" }}>
              {file.name}
            </Typography>
          )}
        </Box>

        {applied?.applied && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Updated {applied.locationsUpdated} location(s).
          </Alert>
        )}

        {preview?.fileErrors.map((issue, i) => (
          <Alert severity="error" sx={{ mt: 2 }} key={i}>
            {issue.message}
          </Alert>
        ))}

        {preview && preview.fileErrors.length === 0 && !applied?.applied && (
          <Alert
            severity={preview.applyable ? (preview.fieldsChanged > 0 ? "info" : "success") : "error"}
            sx={{ mt: 2 }}
          >
            {preview.fieldsChanged > 0 ? (
              <>
                <strong>{preview.fieldsChanged}</strong> change(s) across{" "}
                <strong>{preview.locationsWithChanges}</strong> location(s), from{" "}
                {preview.rowsProcessed} row(s) read.
              </>
            ) : (
              <>No changes found in that file — {preview.rowsProcessed} row(s) read.</>
            )}
            {!preview.applyable && (
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                Nothing will be saved until every row below is fixed.
              </Typography>
            )}
          </Alert>
        )}

        {preview?.ignoredColumns && preview.ignoredColumns.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            These columns were not recognised and were ignored:{" "}
            {preview.ignoredColumns.join(", ")}
          </Alert>
        )}

        {preview?.warnings.map((warning, i) => (
          <Alert severity="warning" sx={{ mt: 2 }} key={i}>
            {warning}
          </Alert>
        ))}

        {rowsWithErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle sx={{ fontSize: "0.85rem" }}>Fix these before saving</AlertTitle>
            <List dense sx={{ listStyleType: "disc", pl: 3 }}>
              {rowsWithErrors.flatMap((row) =>
                row.errors.map((issue, i) => (
                  <ListItem key={`${row.row}-${i}`} sx={{ display: "list-item", p: 0 }}>
                    <Typography variant="caption">
                      Row {issue.row}
                      {row.locationName ? ` (${row.locationName})` : ""}
                      {issue.field ? ` — ${issue.field}` : ""}: {issue.message}
                    </Typography>
                  </ListItem>
                )),
              )}
            </List>
          </Alert>
        )}

        {rowsWithChanges.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {applied?.applied ? "Changes applied" : "Changes to review"}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Current</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>New</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsWithChanges.flatMap((row) =>
                  row.changes.map((change, i) => (
                    <TableRow key={`${row.row}-${change.field}`}>
                      {/* Only label the first change for a location so the eye groups them. */}
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {i === 0 && (
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {row.locationName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Typography variant="caption">{change.field}</Typography>
                        {change.warning && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label="notifies buyers"
                            sx={{ ml: 0.5, height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {renderValue(change.oldValue)}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <ArrowRightAltIcon fontSize="small" sx={{ color: "#8299A5" }} />
                          {renderValue(change.newValue)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )),
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleClose} disabled={applying}>
          {applied?.applied ? "Close" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!canApply || busy}
        >
          {applying
            ? "Saving…"
            : preview && preview.fieldsChanged > 0
              ? `Apply ${preview.fieldsChanged} change(s)`
              : "Apply changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkLocationEditDialog;
